import React, { useState } from "react";
import classNames from "classnames";
import { useBoolean } from "ahooks";
import { IonIcon, IonSpinner } from "@ionic/react";
import { imageOutline } from "ionicons/icons";
import Lightbox from "../Lightbox";
import { ChatAttachment } from "./types";

type ImageAttachmentProps = { attachment: ChatAttachment };

const ImageAttachment = ({ attachment }: ImageAttachmentProps) => {
  const [open, { setTrue, setFalse }] = useBoolean(false);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const isPending = attachment.status === "pending";
  const canOpen = loaded && !!attachment.signedUrl && !isPending;

  if (!attachment.signedUrl) {
    return (
      <div className="relative w-[220px] max-w-full aspect-[5/7] rounded-lg bg-gray-200 animate-pulse flex items-center justify-center">
        <IonIcon icon={imageOutline} className="text-3xl text-gray-400" />
      </div>
    );
  }

  return (
    <>
      <div
        className="relative w-[220px] max-w-full aspect-[5/7] rounded-xl overflow-hidden bg-gray-200"
        onClick={canOpen ? setTrue : undefined}
      >
        {!loaded && !errored && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
            <IonIcon icon={imageOutline} className="text-3xl text-gray-400" />
          </div>
        )}
        {errored ? (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">
            {attachment.fileName}
          </div>
        ) : (
          <img
            src={attachment.signedUrl}
            alt={attachment.fileName}
            loading="lazy"
            onLoad={() => setLoaded(true)}
            onError={() => setErrored(true)}
            className={classNames(
              "absolute inset-0 w-full h-full object-cover transition-opacity duration-200",
              loaded ? "opacity-100" : "opacity-0",
              canOpen ? "cursor-pointer" : "",
            )}
          />
        )}
        {isPending && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <IonSpinner name="dots" />
          </div>
        )}
      </div>
      {open && (
        <Lightbox onClose={setFalse}>
          <img src={attachment.signedUrl} alt={attachment.fileName} className="max-w-full max-h-full object-contain" />
        </Lightbox>
      )}
    </>
  );
};

export default ImageAttachment;
